/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

/**
 * Helper method to determine whether the scroller is the document body or document element.
 */
export const isHTMLScroller = (scrollerElement: HTMLElement) => {
  return scrollerElement === document.body || scrollerElement === document.documentElement;
};

/**
 * Helper method to determine if the vertical scrollbar is on the left side in RTL languages.
 */
export const isLeftHTMLScrollbar = (scrollerHTMLElement: HTMLElement) => {
  return scrollerHTMLElement.getBoundingClientRect().left + scrollerHTMLElement.scrollLeft > 0;
};

/**
 * Helper method to return the current scrollbar height of the scroller element.
 */
export const getScrollbarHeight = (scrollerElement: HTMLElement) => {
  return isHTMLScroller(scrollerElement)
    ? window.innerHeight - scrollerElement.clientHeight
    : scrollerElement.offsetHeight - scrollerElement.clientHeight;
};

/**
 * Helper method to return the current scrollbar width of the scroller element.
 */
export const getScrollbarWidth = (scrollerElement: HTMLElement) => {
  return isHTMLScroller(scrollerElement)
    ? window.innerWidth - scrollerElement.clientWidth
    : scrollerElement.offsetWidth - scrollerElement.clientWidth;
};

/**
 * Helper method to get the horizontal overflow diff of the scroller vs the given element.
 */
export const getHorizontalOverflowDiff = (
  element: HTMLElement,
  scrollerStart: number,
  scrollerEnd: number,
  isRtl: boolean
) => {
  const elementRect = element.getBoundingClientRect();
  if (isRtl) {
    return {
      left: scrollerEnd - elementRect.left,
      right: elementRect.right - scrollerStart
    };
  }
  return {
    left: scrollerStart - elementRect.left,
    right: elementRect.right - scrollerEnd
  };
};

/**
 * Helper method to get the vertical overflow diff of the scroller vs the given element.
 */
export const getVerticalOverflowDiff = (
  element: HTMLElement,
  scrollerTop: number,
  scrollerBottom: number
) => {
  const elementRect = element.getBoundingClientRect();
  return {
    top: scrollerTop - elementRect.top,
    bottom: elementRect.bottom - scrollerBottom
  };
};
